import Typography from 'components/Typography';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import {getFullName} from 'helpers/user';
import styles from '../TransactionDetails.module.scss';
import {get} from 'lodash';
import Link2Icon from './icons/link2';
import StatusBadge from 'components/StatusBadge';
import {formatPrice} from 'utils/format-price';
import classNames from 'classnames';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from 'components/Table';
import {
  ManualChargeMethod,
  ManualPayerType,
  TransactionItem,
  TransactionOrigin,
  TransactionType,
} from 'types/request';
import {CreditCardIcon} from 'pages/PaymentForm/icons';
import {Link} from 'react-router-dom';
import {CardType} from 'helpers/getCardTypeIcon';
import {CreditCardType} from 'components/CreditCardType';
import {
  MANUAL_PAYER_TYPE_OPTIONS_LABELS,
  MANUAL_PAYMENT_TYPE_OPTIONS_LABELS,
  TRANSACTION_ORIGIN_LABELS,
} from 'constants/select-options';
import useCompanyConfig from 'hooks/useCompanyConfig';

interface Props {
  customer: any;
  service: string;
  source: TransactionOrigin;
  amount: number | string;
  items?: TransactionItem[];
  card: string;
  card_type: CardType;
  payment_method: ManualChargeMethod;
  created_at: string;
  transactionType: TransactionType;
  payer_type?: ManualPayerType;
}

function CustomerDetails({
  customer,
  service,
  source,
  amount,
  items,
  card,
  card_type,
  payment_method,
  created_at,
  transactionType,
  payer_type,
}: Props) {
  const {labels} = useCompanyConfig();
  const orderNumber = items?.[0]?.sequential_order_number;
  const showCard = card || card_type;
  const getPaidAmount = (item: TransactionItem) => {
    if (transactionType === 'payable') {
      return item.payable_last_paid
        ? item.payable_last_paid.toFixed(2)
        : item.payable_amount_due.toFixed(2);
    }
    if (transactionType === 'receivable') {
      return item.last_paid
        ? item.last_paid.toFixed(2)
        : item.amount_due.toFixed(2);
    }
    return '';
  };
  return (
    <Card>
      <CardContent>
        <div className={styles.info}>
          <div className={styles.row}>
            <div className={styles.customer_details}>
              <Typography
                component="p"
                variant="label"
                className={styles.strongLabel}
              >
                Customer
              </Typography>

              {customer ? (
                <>
                  <Typography variant="h2" className={styles.customer_name}>
                    {getFullName(customer?.user)}{' '}
                  </Typography>
                  <Link to={`/people/customers/${customer?.id}/edit`}>
                    <Link2Icon width={18} height={18} className={styles.link} />
                  </Link>
                  <Typography variant="p" className={styles.detail}>
                    {get(customer, 'user.email', '')}
                  </Typography>
                  <Typography variant="p" className={styles.detail}>
                    {get(customer, 'user.phone', '')}
                  </Typography>
                  <Typography variant="p" className={styles.detail}>
                    {created_at}
                  </Typography>
                </>
              ) : (
                <Typography variant="h2" className={styles.customer_name}>
                  Unknown Customer
                </Typography>
              )}
            </div>
            <StatusBadge color="gray">
              <Link className={styles.itemLink} to={`/orders/${orderNumber}`}>
                {labels.order} #{orderNumber}
              </Link>
            </StatusBadge>
          </div>
          <div className={styles.separator} />
          <div className={styles.row}>
            <div className={styles.infoCol}>
              <Typography variant="label">Type</Typography>
              <StatusBadge color="default">{service}</StatusBadge>
            </div>
            <div className={styles.infoCol}>
              <Typography variant="label">Source</Typography>
              <StatusBadge color="green">
                {TRANSACTION_ORIGIN_LABELS[source as TransactionOrigin]}
              </StatusBadge>
            </div>
            {payment_method && (
              <div className={styles.infoCol}>
                <Typography variant="label">Payment Type</Typography>
                <StatusBadge color="green">
                  {MANUAL_PAYMENT_TYPE_OPTIONS_LABELS[payment_method]}
                </StatusBadge>
              </div>
            )}
            {!!payer_type && (
              <div className={styles.infoCol}>
                <Typography variant="label">Payer</Typography>
                <StatusBadge color="green">
                  {MANUAL_PAYER_TYPE_OPTIONS_LABELS[payer_type]}
                </StatusBadge>
              </div>
            )}
            <div className={classNames(styles.infoCol, styles.amountCol)}>
              <Typography variant="label">Total amount</Typography>
              <Typography variant="h1" className={styles.amount}>
                {formatPrice(amount)}
              </Typography>
            </div>
          </div>

          {/* Ordered items table */}
          {items && items?.length > 0 && (
            <>
              <div className={styles.separator} />
              <div className={styles.tableWrapper}>
                <Table>
                  <TableHeader>
                    <TableRow className={styles.tableRow}>
                      <TableHead>Item</TableHead>
                      <TableHead align="right">Amount Due</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items?.map((item) => (
                      <TableRow
                        key={`${item?.id}-${item?.drug_name}`}
                        className={styles.tableRow}
                      >
                        <TableCell>{item?.drug_name}</TableCell>
                        <TableCell align="right" className={styles.itemAmount}>
                          ${getPaidAmount(item)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <div className={styles.totalWrapper}>
            <div className={styles.row}>
              <span className={styles.itemLabel}>Sub Total</span>
              <span className={styles.itemValue}>${amount || 0}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.itemLabel}>Discount</span>
              <span className={styles.itemValue}>N/A</span>
            </div>
            <div className={styles.row}>
              <span className={styles.itemLabel}>Tax</span>
              <span className={styles.itemValue}>N/A</span>
            </div>
            <div className={styles.separator} />
            <div className={styles.row}>
              <span className={styles.itemLabelTotal}>Total</span>
              <span className={styles.itemValueTotal}>${amount || 0}</span>
            </div>
            {showCard && (
              <>
                <div className={styles.separator} />
                <div className={styles.row}>
                  <span className={styles.itemLabel}>
                    <CreditCardIcon />
                    Card
                  </span>
                  <span
                    className={classNames(styles.itemValue, styles.card_type)}
                  >
                    **** **** **** {card}
                    <CreditCardType type={card_type} />
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default CustomerDetails;
